<template>
  <div class="send-page">
    <div class="left-panel">
      <div class="form-card">
        <h2 class="panel-title">Transfert soviétique</h2>

        <div class="storage">
          <div class="storage-legend">
            Espace dispo : {{ formatSize(totalSize) }} / {{ formatSize(storage.freeBytes) }}
          </div>
          <div
              class="storage-bar"
              role="progressbar"
              :aria-valuemin="0"
              :aria-valuemax="100"
              :aria-valuenow="Math.round(displayFillPct)"
          >
            <div class="storage-bar-fill" :style="{ width: displayFillPct + '%' }"></div>
          </div>
        </div>

        <p class="total-size">Taille totale (sélection) : {{ formatSize(totalSize) }}</p>

        <div class="field">
          <label class="checkbox">
            <input type="checkbox" v-model="anonymous"/>
            <span>Envoyer anonymement</span>
          </label>
        </div>

        <div class="field" v-if="!anonymous">
          <input class="email-input" type="email" v-model.trim="email" placeholder="Adresse e-mail"
                 autocomplete="email"/>
        </div>

        <div class="field">
          <select class="retention-select" v-model="retention">
            <option value="first_download">jusqu'au premier téléchargement</option>
            <option value="1h">1h</option>
            <option value="1d">1 jour</option>
            <option value="1w">1 semaine</option>
            <option value="1m">1 mois</option>
          </select>
        </div>

        <button class="send-btn" :disabled="!canSend" @click="sendFiles">Envoyer</button>
      </div>
    </div>

    <div class="right-panel">
      <div
          class="drop-zone"
          :class="{ 'drop-zone--active': isDragOver }"
          @dragover.prevent="isDragOver = true"
          @dragleave.prevent="isDragOver = false"
          @drop.prevent="handleDrop"
          @click="$refs.fileInput.click()"
      >
        <div class="drop-content">
          <div class="drop-icon" aria-hidden="true">📂</div>
          <p class="drop-text">Déposez vos fichiers ici ou cliquez pour les ajouter</p>
        </div>
        <input ref="fileInput" type="file" multiple @change="handleFileInput" class="file-input"/>
      </div>

      <ul class="file-list" v-if="files.length">
        <li v-for="(file, index) in files" :key="index" class="file-item">
          <div class="file-meta">
            <strong class="file-name">{{ file.name }}</strong>
            <span class="file-size">{{ formatSize(file.size) }}</span>
          </div>
          <button class="delete-btn" @click="removeFile(index)" aria-label="Supprimer">✖</button>
        </li>
      </ul>
    </div>

    <UploadModal
        :visible="showModal"
        :progress="progress"
        :transferred="uploaded"
        :total="totalToSend"
        :speed-bps="speedBps"
        :eta-sec="etaSec"
        :upload-id="uploadId"
        :manage-id="manageId"
        :email-linked="emailLinked"
        :status="status"
        :share-link="shareLink"
        :manage-link="manageLink"
        @close="closeModal"
    />
  </div>
</template>

<script>
import axios from 'axios'
import UploadModal from '@/components/UploadModal.vue'

const API_BASE = process.env.VUE_APP_API_URL || 'http://localhost:3000'

export default {
  name: 'SendView',

  components: {
    UploadModal
  },

  data() {
    return {
      files: [],
      isDragOver: false,
      showModal: false,
      progress: 0,
      uploaded: 0,
      totalToSend: 0,
      speedBps: 0,
      etaSec: 0,
      uploadId: '',
      manageId: '',
      emailLinked: false,
      shareLink: '',
      manageLink: '',
      status: 'idle',
      anonymous: false,
      email: '',
      retention: '1h',
      storage: {
        totalBytes: 0,
        usedBytes: 0,
        freeBytes: 0,
        loading: false,
        error: null
      },
      storageTimer: null
    }
  },

  computed: {
    totalSize() {
      return this.files.reduce((a, f) => a + f.size, 0)
    },

    displayFillPct() {
      const cap = this.storage.freeBytes || 0
      if (cap <= 0) return 0
      const deposited = Math.max(0, Math.min(this.totalSize, cap))
      return (deposited / cap) * 100
    },

    selectionExceeds() {
      return this.storage.totalBytes > 0 && this.totalSize > this.storage.freeBytes
    },

    canSend() {
      if (this.status === 'uploading') return false
      if (!this.files.length) return false
      if (!this.anonymous && this.email.length === 0) return false
      if (!this.anonymous && this.email.length > 0 && !this.validEmail(this.email)) return false
      return !this.selectionExceeds;
    }
  },

  mounted() {
    this.fetchStorage()
    this.storageTimer = setInterval(this.fetchStorage, 20000)
  },

  beforeUnmount() {
    if (this.storageTimer) clearInterval(this.storageTimer)
  },

  methods: {
    validEmail(v) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
    },

    async fetchStorage() {
      this.storage.loading = true
      this.storage.error = null
      try {
        const {data} = await axios.get(`${API_BASE}/storage`, {timeout: 3000})
        this.storage.totalBytes = Number(data?.totalBytes) || 0
        this.storage.usedBytes = Number(data?.usedBytes) || 0
        this.storage.freeBytes = Number(data?.freeBytes) || 0
      } catch (_e) {
        this.storage.error = 'Impossible de récupérer l’espace disque'
      } finally {
        this.storage.loading = false
      }
    },

    handleDrop(e) {
      this.isDragOver = false
      const dropped = Array.from(e.dataTransfer.files || [])
      if (dropped.length) this.files.push(...dropped)
    },

    handleFileInput(e) {
      const selected = Array.from(e.target.files || [])
      if (selected.length) this.files.push(...selected)
      e.target.value = ''
    },

    removeFile(i) {
      this.files.splice(i, 1)
    },

    formatSize(b) {
      if (b < 1024) return b + ' B'
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB'
      if (b < 1024 * 1024 * 1024) return (b / (1024 * 1024)).toFixed(1) + ' MB'
      return (b / (1024 * 1024 * 1024)).toFixed(1) + ' GB'
    },

    closeModal() {
      this.showModal = false
      this.status = 'idle'
      this.progress = 0
      this.uploaded = 0
      this.speedBps = 0
      this.etaSec = 0
      this.uploadId = ''
      this.manageId = ''
      this.emailLinked = false
      this.shareLink = ''
      this.manageLink = ''
      this.fetchStorage()
    },

    async sendFiles() {
      if (!this.canSend) return
      const form = new FormData()
      this.files.forEach(f => form.append('files', f, f.name))
      form.append('anonymous', String(this.anonymous))
      form.append('retention', this.retention)
      if (!this.anonymous && this.email) form.append('email', this.email)

      this.totalToSend = this.totalSize
      this.uploaded = 0
      this.progress = 0
      this.speedBps = 0
      this.status = 'uploading'
      this.showModal = true
      const start = Date.now()
      let lastT = start, lastL = 0, smooth = 0, alpha = 0.2
      try {
        const res = await axios.post(`${API_BASE}/upload`, form, {
          headers: {'x-total-size': String(this.totalToSend)},
          onUploadProgress: (e) => {
            const now = Date.now()
            const loaded = e.loaded || 0
            const total = e.total || this.totalToSend || 1
            this.uploaded = loaded
            this.progress = Math.min(1, loaded / total)
            const dt = (now - lastT) / 1000 || 1
            const inst = (loaded - lastL) / dt
            smooth = smooth ? smooth * (1 - alpha) + inst * alpha : inst
            this.speedBps = smooth
            this.etaSec = smooth > 0 ? (total - loaded) / smooth : Infinity
            lastT = now
            lastL = loaded
          },
          validateStatus: s => s >= 200 && s < 300
        })
        this.shareLink = res.data?.link || ''
        this.manageLink = res.data?.manageLink || ''
        this.emailLinked = !!res.data?.emailLinked
        this.uploadId = ''
        this.manageId = ''
        this.progress = 1
        this.status = 'done'
        this.files = []
        this.isDragOver = false
      } catch (err) {
        this.status = 'error'
        console.error('Upload error:', err)
      } finally {
        this.fetchStorage()
      }
    }
  }
}
</script>

<style>
:root {
  --soviet-red: #8B0000;
  --soviet-red-dark: #5a0000;
  --gold: #C9A74D;
  --gold-light: #f6d676;
  --gold-08: rgba(201, 167, 77, 0.08);
  --gold-16: rgba(201, 167, 77, 0.16);
  --gold-28: rgba(201, 167, 77, 0.28);
}

body {
  background: #fff;
}
</style>

<style scoped>
.send-page {
  display: flex;
  height: calc(100vh - 70px);
}

.left-panel, .right-panel {
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
}

.left-panel {
  width: 40%;
}

.right-panel {
  width: 60%;
  flex-direction: column;
  gap: 1.2rem;
  padding: 2rem;
}

.form-card {
  width: min(420px, 90%);
  padding: 1.2rem 1.4rem;
  border: 3px dashed var(--gold);
  border-radius: 14px;
  background: var(--gold-08);
  text-align: center;
  display: grid;
  gap: .8rem;
}

.panel-title {
  font-family: 'Impact', sans-serif;
  font-size: 1.8rem;
  color: var(--soviet-red-dark);
  margin: 0;
}

.total-size {
  font-weight: 600;
  color: var(--soviet-red-dark);
  margin: 0;
}

.field {
  display: flex;
  justify-content: center;
}

.checkbox {
  display: inline-flex;
  gap: .5rem;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.email-input, .retention-select {
  width: 100%;
  max-width: 320px;
  padding: .6rem .8rem;
  border-radius: 10px;
  border: 1px solid var(--gold-16);
  background: #fff;
}

.send-btn {
  width: 100%;
  background: var(--gold-08);
  color: var(--soviet-red-dark);
  border: 3px dashed var(--gold);
  padding: .85rem 1.4rem;
  font-size: 1.05rem;
  border-radius: 14px;
  cursor: pointer;
  transition: background-color .2s ease, border-color .2s ease, color .2s ease;
}

.send-btn:hover {
  background: var(--gold-16);
  border-color: var(--gold-light);
}

.send-btn:active {
  background: var(--gold-28);
  border-color: var(--gold-light);
}

.send-btn:disabled {
  opacity: .5;
  cursor: not-allowed;
}

.drop-zone {
  width: min(900px, 92%);
  min-height: 340px;
  border: 3px dashed var(--gold);
  border-radius: 14px;
  background: var(--gold-08);
  cursor: pointer;
  transition: background-color .2s ease, border-color .2s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
}

.drop-zone:hover {
  background: var(--gold-16);
}

.drop-zone--active {
  background: var(--gold-28);
  border-color: var(--gold-light);
}

.drop-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: .35rem;
  text-align: center;
}

.drop-icon {
  font-size: 3rem;
  color: var(--gold);
}

.drop-text {
  color: var(--soviet-red-dark);
  font-weight: 700;
}

.file-input {
  display: none;
}

.file-list {
  width: min(900px, 92%);
  max-height: 260px;
  overflow-y: auto;
  list-style: none;
  margin: 0;
  padding: 0;
  user-select: none;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: .65rem .8rem;
  margin: .5rem 0;
  border: 1px solid var(--gold-16);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.025);
  transition: background-color .15s ease, border-color .15s ease;
}

.file-item:hover {
  background: rgba(0, 0, 0, 0.06);
  border-color: var(--gold-28);
}

.file-meta {
  display: flex;
  align-items: baseline;
  gap: .6rem;
  min-width: 0;
}

.file-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #5a3f00;
  font-weight: 700;
}

.file-size {
  white-space: nowrap;
  font-size: .9rem;
  color: #7a5a0a;
  opacity: .95;
}

.delete-btn {
  background: transparent;
  border: 1px solid var(--gold-16);
  color: var(--soviet-red-dark);
  border-radius: 8px;
  padding: .25rem .5rem;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease;
}

.delete-btn:hover {
  background: var(--gold-16);
  border-color: var(--gold-28);
  color: #000;
}

.storage {
  display: grid;
  gap: .4rem;
  text-align: center;
}

.storage-legend {
  font-weight: 700;
  color: var(--soviet-red-dark);
}

.storage-bar {
  position: relative;
  width: 100%;
  height: 12px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid var(--gold-16);
  overflow: hidden;
}

.storage-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--gold) 0%, var(--gold-light) 100%);
  transition: width .25s ease;
}
</style>
